/**
 * UIContext - Ephemeral UI state management
 */

import React, { createContext, useContext, useState, useCallback } from 'react';

const UIContext = createContext();

export function UIProvider({ children }) {
    // Chat state — restore currentChatId from sessionStorage to survive hot reloads
    const [currentChatId, setCurrentChatIdRaw] = useState(() => {
        try { return sessionStorage.getItem('restack_currentChatId') || null; }
        catch { return null; }
    });
    const [isChatStreaming, setIsChatStreaming] = useState(false);
    const [chatComposerValue, setChatComposerValue] = useState('');

    // Wrap setCurrentChatId to also persist to sessionStorage
    const setCurrentChatId = useCallback((chatId) => {
        setCurrentChatIdRaw(chatId);
        try {
            if (chatId) sessionStorage.setItem('restack_currentChatId', chatId);
            else sessionStorage.removeItem('restack_currentChatId');
        } catch { /* ignore */ }
    }, []);

    // Chat navigation
    const switchToChat = useCallback((chatId) => {
        setCurrentChatId(chatId);
    }, [setCurrentChatId]);

    // Reset UI state
    const resetUIState = useCallback(() => {
        setCurrentChatId(null);
        setIsChatStreaming(false);
    }, [setCurrentChatId]);

    const value = {
        // Chat state
        currentChatId,
        setCurrentChatId,
        isChatStreaming,
        setIsChatStreaming,
        chatComposerValue,
        setChatComposerValue,

        // Chat operations
        switchToChat,

        // Utility
        resetUIState
    };

    return (
        <UIContext.Provider value={value}>
            {children}
        </UIContext.Provider>
    );
}

export function useUI() {
    const context = useContext(UIContext);
    if (!context) {
        throw new Error('useUI must be used within a UIProvider');
    }
    return context;
}
